import { ProviderType } from 'features/connections'
import { Feature } from 'geojson'
export const SECTION_NAME = 'catalog'
export const SEARCH_PAGE = 'browser'
export const DATASET_PAGE = 'dataset'

export const FILTER_OPTION_GLOBAL_ID = 'glo'

export const FILTER_UPDATE_DEBOUNCE_TIME = 0

export const QUERIES_CACHE_TIME = 24 * 60 * 60 * 1000
export const RESULT_QUERY_STALE_TIME = 0
export const OPTIONS_QUERY_STALE_TIME = 500
export const SEARCH_QUERY_STALE_TIME = 500

type CategoryColorSchema = {
  backgroundColor: string
  map?: {
    sequential: string
    qualitative: string
  }
}

export const CATEGORIES_COLOR_SCHEMA: Record<string, CategoryColorSchema> = {
  behavioral: {
    backgroundColor: 'rgba(254, 136, 177, 1)', // Pastel 06
    map: {
      sequential: 'Burg',
      qualitative: 'Vivid'
    }
  },
  demographics: {
    backgroundColor: 'rgba(248, 156, 116, 1)', // Pastel 02,
    map: {
      sequential: 'SunsetDark',
      qualitative: 'Prism'
    }
  },
  derived: {
    backgroundColor: 'rgba(211, 180, 132, 1)', // Pastel 10
    map: {
      sequential: 'BrwnYl',
      qualitative: 'Antique'
    }
  },
  environmental: {
    backgroundColor: 'rgba(158, 185, 243, 1)', // Pastel 05
    map: {
      sequential: 'Emrld',
      qualitative: 'Safe'
    }
  },
  financial: {
    backgroundColor: 'rgba(139, 224, 164, 1)', // Pastel 08
    map: {
      sequential: 'TealGrn',
      qualitative: 'Pastel'
    }
  },
  geography: {
    backgroundColor: 'rgba(135, 197, 95, 1)', // Pastel 04
    map: {
      sequential: 'BurgYl',
      qualitative: 'Safe'
    }
  },
  housing: {
    backgroundColor: 'rgba(102, 197, 204, 1)', // Pastel 00
    map: {
      sequential: 'Teal',
      qualitative: 'Bold'
    }
  },
  human_mobility: {
    backgroundColor: 'rgba(220, 176, 242, 1)', // Pastel 03
    map: {
      sequential: 'Purp',
      qualitative: 'Prism'
    }
  },
  infrastructure: {
    backgroundColor: 'rgba(180, 151, 231, 1)', // Pastel 09
    map: {
      sequential: 'PurpOr',
      qualitative: 'Prism'
    }
  },
  points_of_interest: {
    backgroundColor: 'rgba(246, 207, 113, 1)', // Pastel 01
    map: {
      sequential: 'Sunset',
      qualitative: 'Bold'
    }
  },
  road_traffic: {
    backgroundColor: 'rgba(179, 179, 179, 1)', // Pastel 11
    map: {
      sequential: 'Temps',
      qualitative: 'Bold'
    }
  },
  covid19: {
    backgroundColor: 'rgba(225, 227, 228, 1)', // Grey 100
    map: {
      sequential: 'OrYel',
      qualitative: 'Vivid'
    }
  },
  all_categories: {
    backgroundColor: 'rgba(3, 111, 226, 0.1)'
  }
}

export enum FilterTypesElement {
  CHECKBOX = 'checkbox',
  TAG = 'tag'
}
export interface FilterElementOption {
  id: string
  name: string
  datasetCount: number
  iconName?: string
  hasIcon?: boolean
  color?: string
  secondaryText?: string
}
export interface FilterElement {
  id: string
  expanded: boolean
  type: FilterTypesElement
  options: FilterElementOption[]
}

export interface CatalogFilters {
  countries?: string[]
  categories?: string[]
  licenses?: string[]
  providers?: string[]
  sources?: string[]
  useCases?: string[]
  temporalAggs?: string[]
  spatialAggs?: string[]
}

export enum CatalogOrder {
  AZ = 'alphabetical',
  RANK = 'rank',
  MOST_RECENT = 'recent'
}
export enum CatalogSubscriptionStatus {
  ALL = 'all',
  SUBSCRIBED = 'subscribed',
  REQUESTED = 'requested',
  EXPIRES_SOON = 'expiresSoon'
}

export enum CatalogSubscriptionRequestStatus {
  ACTIVE = 'active',
  CANCEL = 'cancel'
}

export enum CatalogEntityTypes {
  Dataset = 'dataset',
  Geography = 'geography'
}

export interface CatalogPagination {
  page: number
  limit: number
  total: number
}

export enum CatalogSubscriptionType {
  SAMPLE = 'sample',
  DATA = 'data',
  GEOGRAPHY = 'geography'
}

export interface CatalogSubscription {
  subscriptionType: 'public' | 'premium'
  datasetType: CatalogSubscriptionType
  datasetId: string
  expiresAt: string | null
  implicit: boolean
  tableName: string
  availableIn: AvailableIn[]
}
export interface AvailableIn {
  provider: ProviderType
  regions: string[]
}

export interface UseCase {
  id: string
  description: string
  name: string
}
export interface CatalogDataset {
  categoryId: string
  thumbnail?: string
  categoryName: string
  countryId: string
  countryName: string
  description: string
  defaultViz?: string
  documentationLink?: string
  geographyId: string
  geographyName: string
  geographySlug?: string
  id: string
  isGeography: boolean
  licenseId: string
  licenseName: string
  licensesDescription?: string
  licensesLink?: string
  name: string
  placetypeId: string
  placetypeName: string
  providerId: string
  providerLogo: string
  providerName: string
  providerDescription?: string
  providerWebsite?: string
  slug: string
  spatialAggregationId: string
  spatialAggregationName: string
  temporalAggregation: string
  temporalAggregationId: string
  updateFrequency: string
  version: string
  sampleInfo?: { id?: string; source?: string; slug?: string; providerId?: string }
  useCases: UseCase[]
  status: CatalogSubscriptionStatus
}

export interface CatalogDatasetSample {
  cols: {
    dbType: string
    description: string
    name: string
    value: string
    rank: number
  }[]
  totalRows?: number
  values?: Record<string, unknown>[]
  variableGroups: string[]
}

export interface CatalogProvider {
  id: string
  name: string
  rank: number
  datasetCount: number
}

export interface CatalogCategories {
  id: string
  name: string
  rank: number
  datasetCount: number
}

export interface CatalogDatasetFAQ {
  id: string
  answer: string
  question: string
}

export interface CatalogDatasetVariable {
  dbType: string
  description: string
  name: string
  value: string
}

export interface CatalogEntityResponse {
  totalResults: number
  results: CatalogDataset[]
}

export interface DataCatalogQueryParams {
  filters?: CatalogFilters
  orderBy?: CatalogOrder
  datasets?: string[]
  selectedCountry?: string
  fullTextSearch?: string
  pagination?: { page: number; limit: number }
}

export interface GeoParquetSchemaField {
  name: string
  type: string
  nullable: boolean
  metadata: {
    compresion: string
    encoding: string
    optional: string
    precision: string
    scale: string
    repeated: string
    type: string
    typeLength: string
  }
}

export interface GeoParquetSchema {
  fields: GeoParquetSchemaField[]
  metadata: Record<string, string>
  shape: 'geojson-table'
  type: 'FeatureCollection'
}
export interface GeoParquet {
  features: Feature[]
  schema: GeoParquetSchema
}
